import { api, setToken } from './apiClient';
import { subscriptionService, type BackendSubscriptionSnapshot } from './subscriptionService';

export type LoginPayload = { email: string; password: string };
export type RegisterPayload = {
  email: string;
  password: string;
  name: string;
  crp?: string;
  clinicName?: string;
  /** Token do convite: define o papel do novo usuário na clínica. */
  inviteToken?: string;
  acceptTerms: boolean;
};

export type AuthUser = BackendSubscriptionSnapshot & {
  id: string;
  tenantId?: string | null;
  email: string;
  name: string;
  crp?: string | null;
  clinicName?: string | null;
  avatarUrl?: string | null;
  professionalPhoto?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  cnpj?: string | null;
  /** Bloco #45: WhatsApp pessoal e da clínica (destino das notificações automáticas). */
  whatsapp?: string | null;
  clinicWhatsapp?: string | null;
  /** Papel na clínica, definido no cadastro (convite) e persistido no backend. */
  teamRole?: 'ADMINISTRADOR' | 'PROFISSIONAL' | 'RECEPCIONISTA' | null;
  /** Administrador da plataforma (PLATFORM_ADMIN_EMAILS na API): vê o painel "Usuários da Plataforma". */
  isPlatformAdmin?: boolean;
  /** Primeiro acesso (blocos 30/31): versão dos Termos e do Checklist já lidos por este usuário. */
  legalTermsReadVersion?: number | null;
  legalTermsReadAt?: string | null;
  lgpdChecklistReadVersion?: number | null;
  lgpdChecklistReadAt?: string | null;
  /**
   * Identidade da clínica (do administrador do tenant), enviada pelo /auth/me
   * para perfis que não são administrador exibirem em modo somente leitura.
   */
  clinic?: {
    clinicName?: string | null;
    cnpj?: string | null;
    phone?: string | null;
    clinicWhatsapp?: string | null;
    email?: string | null;
    website?: string | null;
    address?: string | null;
    avatarUrl?: string | null;
  } | null;
};

export type AuthResponse = {
  access_token: string;
  user: AuthUser;
};

export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const res = await api.post<AuthResponse>('/auth/login', payload, { skipAuth: true });
  if (res.access_token) setToken(res.access_token);
  subscriptionService.syncFromBackend(res.user);
  return res;
}

export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const res = await api.post<AuthResponse>('/auth/register', payload, { skipAuth: true });
  if (res.access_token) setToken(res.access_token);
  subscriptionService.syncFromBackend(res.user);
  return res;
}

export type MeResponse = AuthUser & {
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  subscriptionStatus?: string;
  subscriptionEndDate?: string | null;
  emailVerifiedAt?: string | null;
  termsAcceptedAt?: string | null;
};

export async function me(): Promise<MeResponse> {
  const user = await api.get<MeResponse>('/auth/me');
  subscriptionService.syncFromBackend(user);
  return user;
}

/** Bloco 39: token do link de recuperação (`/reset-password?token=` ou `?reset=`). */
export function readResetTokenFromUrl(search = window.location.search, pathname = window.location.pathname): string | null {
  const params = new URLSearchParams(search);
  const token = params.get('reset') || (pathname.replace(/\/+$/, '') === '/reset-password' ? params.get('token') : null);
  return token?.trim() || null;
}

export function clearResetTokenFromUrl(): void {
  window.history.replaceState({}, '', '/');
}

export async function confirmEmail(token: string): Promise<{ success: boolean; message: string }> {
  return api.post('/auth/confirm-email', { token }, { skipAuth: true });
}

export async function resendConfirmation(email: string): Promise<{ success: boolean; message: string }> {
  return api.post('/auth/resend-confirmation', { email }, { skipAuth: true });
}

export async function forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
  return api.post('/auth/forgot-password', { email }, { skipAuth: true });
}

export async function resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
  return api.post('/auth/reset-password', { token, password }, { skipAuth: true });
}
